/**
 * Pure H OS · Sprint 1 · Component Library.
 *
 * Themed under "Pure H Executive Warm v1" — tokens live in
 * `../theme.css` and are consumed here via CSS variables. Structure,
 * props, layout, and behaviour are unchanged from the Sprint 1 seed.
 *
 * TOKEN MAP (see /app/frontend/src/pureh/theme.css):
 *   surface        → --ph-surface / --ph-surface-alt / --ph-surface-card
 *   ink            → --ph-ink-strong / --ph-ink-default / --ph-ink-muted
 *   brand          → --ph-brand-primary (warm gold) + --ph-brand-accent
 *   danger         → --ph-danger      (#A94442)
 *   success        → --ph-success     (#2F7D5A)
 *   warning        → --ph-warning     (#C68A2F)
 *   ai             → --ph-ai          (#4F5DB8)
 *   spacing scale  → Tailwind default (4 / 8 / 12 / 16 / 24 / 32 / 48 / 64)
 *   radius         → --ph-radius-{sm,md,lg,xl}
 *   shadow         → --ph-shadow-card / --ph-shadow-elevated
 *   fonts          → --ph-display-font (Cormorant Garamond) + --ph-body-font (Inter)
 */
import { ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";

// ── Card ─────────────────────────────────────────────────────────
export function Card({
  children, className = "", padded = true, testId,
}: { children: ReactNode; className?: string; padded?: boolean; testId?: string }) {
  return (
    <div
      data-testid={testId}
      className={"border " + (padded ? "p-5 " : "") + className}
      style={{
        background: "var(--ph-surface-card)",
        borderColor: "var(--ph-border-subtle)",
        borderRadius: "var(--ph-radius-md)",
        boxShadow: "var(--ph-shadow-card)",
      }}
    >
      {children}
    </div>
  );
}

// ── Section header (eyebrow + title + optional actions) ─────────
export function SectionHeader({
  eyebrow, title, subtitle, action,
}: { eyebrow?: string; title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="flex items-end justify-between mb-4">
      <div>
        {eyebrow && (
          <div
            className="mb-1"
            style={{
              fontSize: "var(--ph-size-caption)",
              textTransform: "uppercase",
              letterSpacing: "var(--ph-tracking-eyebrow)",
              color: "var(--ph-ink-muted)",
              fontWeight: 600,
            }}
          >
            {eyebrow}
          </div>
        )}
        <div
          style={{
            fontFamily: "var(--ph-display-font)",
            fontSize: "var(--ph-size-h3)",
            fontWeight: 600,
            color: "var(--ph-ink-strong)",
            letterSpacing: "var(--ph-tracking-title)",
            lineHeight: 1.2,
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div className="text-xs mt-1" style={{ color: "var(--ph-ink-soft)" }}>
            {subtitle}
          </div>
        )}
      </div>
      {action && <div className="flex items-center gap-2">{action}</div>}
    </div>
  );
}

// ── Trend delta chip ────────────────────────────────────────────
export function DeltaChip({
  delta, trend,
}: { delta: number; trend: "up" | "down" | "flat" }) {
  const Icon = trend === "up" ? ArrowUpRight : trend === "down" ? ArrowDownRight : Minus;
  const palette = trend === "up"
    ? { bg: "var(--ph-success-tint)",  fg: "var(--ph-success)" }
    : trend === "down"
    ? { bg: "var(--ph-danger-tint)",   fg: "var(--ph-danger)"  }
    : { bg: "var(--ph-neutral-tint)",  fg: "var(--ph-ink-muted)" };
  const sign = trend === "up" ? "+" : trend === "down" ? "" : "±";
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium"
      style={{
        background: palette.bg,
        color: palette.fg,
        borderRadius: "var(--ph-radius-sm)",
      }}
    >
      <Icon size={11} strokeWidth={2.2} />
      {sign}{Math.abs(delta).toFixed(1)}%
    </span>
  );
}

// ── Severity badge ──────────────────────────────────────────────
export function SeverityBadge({ severity }: { severity: "high" | "medium" | "low" }) {
  const palette = severity === "high"
    ? { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",  label: "High"   }
    : severity === "medium"
    ? { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)", label: "Medium" }
    : { bg: "var(--ph-success-tint)", fg: "var(--ph-success)", label: "Low"    };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold"
      style={{
        background: palette.bg,
        color: palette.fg,
        borderRadius: "var(--ph-radius-sm)",
        textTransform: "uppercase",
        letterSpacing: "0.14em",
      }}
    >
      {palette.label}
    </span>
  );
}

// ── Confidence bar ──────────────────────────────────────────────
export function ConfidenceBar({ value }: { value: number }) {
  return (
    <div
      className="w-full h-1 overflow-hidden"
      style={{
        background: "var(--ph-border-subtle)",
        borderRadius: "var(--ph-radius-sm)",
      }}
    >
      <div
        className="h-full"
        style={{
          width: `${Math.round(value * 100)}%`,
          background: "var(--ph-brand-primary)",
        }}
      />
    </div>
  );
}

// ── Score dial (department health) ──────────────────────────────
export function ScoreDial({ score }: { score: number }) {
  const color =
    score >= 80 ? "var(--ph-success)" :
    score >= 65 ? "var(--ph-warning)" :
                  "var(--ph-danger)";
  const pct = Math.min(100, Math.max(0, score));
  const circumference = 2 * Math.PI * 16;
  const dash = (pct / 100) * circumference;
  return (
    <svg width="42" height="42" viewBox="0 0 40 40" aria-hidden="true">
      <circle cx="20" cy="20" r="16" fill="none" stroke="var(--ph-border-subtle)" strokeWidth="3.5" />
      <circle
        cx="20" cy="20" r="16" fill="none" stroke={color} strokeWidth="3.5"
        strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round"
        transform="rotate(-90 20 20)"
      />
      <text
        x="20" y="24" textAnchor="middle"
        fontSize="11" fontWeight="700"
        fill="var(--ph-ink-strong)"
        style={{ fontFamily: "var(--ph-body-font)" }}
      >
        {score}
      </text>
    </svg>
  );
}

// ── Empty state ─────────────────────────────────────────────────
export function EmptyState({ title, hint, message, body, icon: Icon, testId }: {
  title: string; hint?: string; message?: string; body?: string;
  icon?: React.ComponentType<{ size?: number | string; style?: React.CSSProperties }>;
  testId?: string;
}) {
  const sub = hint || message || body;
  return (
    <div className="py-8 text-center" data-testid={testId}>
      {Icon && (
        <div className="flex justify-center mb-2">
          <Icon size={20} style={{ color: "var(--ph-ink-soft)" }} />
        </div>
      )}
      <div
        className="text-sm font-medium"
        style={{ color: "var(--ph-ink-muted)" }}
      >
        {title}
      </div>
      {sub && (
        <div
          className="text-xs mt-1"
          style={{ color: "var(--ph-ink-soft)" }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

// ── Button (minimal, brand-neutral) ─────────────────────────────
export function Button({
  children, onClick, variant = "primary", type = "button", testId, disabled = false,
}: {
  children: ReactNode; onClick?: () => void;
  variant?: "primary" | "secondary" | "ghost";
  type?: "button" | "submit"; testId?: string; disabled?: boolean;
}) {
  const base = "inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50";

  const styles =
    variant === "primary"
      ? {
          background: "var(--ph-brand-primary)",
          color: "#1F2025",
          border: "1px solid var(--ph-brand-primary)",
        }
      : variant === "secondary"
      ? {
          background: "transparent",
          color: "var(--ph-ink-default)",
          border: "1px solid var(--ph-border-strong)",
        }
      : {
          background: "transparent",
          color: "var(--ph-ink-muted)",
          border: "1px solid transparent",
        };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={base}
      style={{
        ...styles,
        borderRadius: "var(--ph-radius-sm)",
        fontFamily: "var(--ph-body-font)",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        if (variant === "primary") {
          (e.currentTarget as HTMLButtonElement).style.background = "var(--ph-brand-accent)";
          (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--ph-brand-accent)";
        } else if (variant === "secondary") {
          (e.currentTarget as HTMLButtonElement).style.background = "var(--ph-hover-bg)";
        } else {
          (e.currentTarget as HTMLButtonElement).style.background = "var(--ph-hover-bg)";
        }
      }}
      onMouseLeave={(e) => {
        if (variant === "primary") {
          (e.currentTarget as HTMLButtonElement).style.background = "var(--ph-brand-primary)";
          (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--ph-brand-primary)";
        } else {
          (e.currentTarget as HTMLButtonElement).style.background = "transparent";
        }
      }}
    >
      {children}
    </button>
  );
}

// ── Input (single primitive used for auth + search) ────────────
export function TextInput({
  value, onChange, placeholder, type = "text", testId, autoFocus,
}: {
  value: string; onChange: (v: string) => void; placeholder?: string;
  type?: string; testId?: string; autoFocus?: boolean;
}) {
  return (
    <input
      type={type} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} data-testid={testId} autoFocus={autoFocus}
      className="w-full px-3 py-2 text-sm focus:outline-none"
      style={{
        color: "var(--ph-ink-strong)",
        background: "var(--ph-surface-card)",
        border: "1px solid var(--ph-border-strong)",
        borderRadius: "var(--ph-radius-sm)",
        fontFamily: "var(--ph-body-font)",
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "var(--ph-brand-primary)";
        e.currentTarget.style.boxShadow = "var(--ph-focus-ring)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "var(--ph-border-strong)";
        e.currentTarget.style.boxShadow = "none";
      }}
    />
  );
}
